import { useMemo } from 'react';
import { childrenOf, isAdminOptInFeature, type FeatureId } from '@sequesto-os/shared';
import { useAuth } from './useAuth';

/**
 * Feature-level access for the current user. Admins always pass, except for
 * opt-in features (e.g. AI Visibility) which are hidden for everyone until
 * enabled in Settings — those rely on the `access` list the API computed.
 * Non-admins always go through the `access` list (role defaults + overrides).
 *
 * `canAccessArea` is for nav: a parent area is visible if the user can reach the
 * area itself OR any of its children (so a CS-only user still sees "Dashboard").
 */
export function useAccess() {
  const { user } = useAuth();

  return useMemo(() => {
    const isAdmin = user?.role === 'admin';
    const granted = new Set(user?.access ?? []);

    const canAccess = (feature: FeatureId): boolean =>
      granted.has(feature) || (isAdmin && !isAdminOptInFeature(feature));

    const canAccessArea = (feature: FeatureId): boolean =>
      canAccess(feature) || childrenOf(feature).some((child) => canAccess(child.id as FeatureId));

    return { isAdmin, canAccess, canAccessArea };
  }, [user]);
}
